import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BehaviorSubject, first } from 'rxjs';

import { ProfileService } from 'app/shared/services/profile.service';
import { ProfileStateService } from 'app/shared/services/profile-state.service';

import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { BillingHistoryComponent } from './billing-history/billing-history.component';
import { SwitchComponent } from 'app/shared/components/switch/switch.component';

import { IBillingHistory, IUserSubscription } from 'app/interfaces/profile.interfaces';
import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';

import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { ESubscriptionState } from 'app/enums/purchases.enums';
import { EProfileSectionTitle } from 'app/enums/profile.enums';

@Component({
  selector: 'stxt-subscription-management',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, BillingHistoryComponent, SwitchComponent],
  templateUrl: './subscription-management.component.html',
  styleUrl: './subscription-management.component.scss'
})
export class SubscriptionManagementComponent implements OnInit {
  @Input() selectedSubscription: IUserSubscription;
  @Output() autoRenewChange: EventEmitter<void> = new EventEmitter<void>();
  public rightNavSvg: ISvgConfig = { name: 'arrow_right', fill: ESvgTypes.None };
  public infoSvg: ISvgConfig = { name: 'info', fill: ESvgTypes.None };
  public transactionsHistory: IBillingHistory[];
  public transactionsLoaded: boolean = false;
  protected readonly SubscriptionState = ESubscriptionState;
  protected readonly EProfileSectionTitle = EProfileSectionTitle;
  public profileSettingsTitle$: BehaviorSubject<string> = this.profileService.profileActiveSection$;
  public subscriptionBenefits: { svg: ISvgConfig; title: string; subtitle: string }[] = [
    {
      svg: { name: 'speech-balloon', fill: ESvgTypes.None },
      title: '24/7/365 chat',
      subtitle: 'Always responsive, never be left waiting again. Any time, anywhere.'
    },
    {
      svg: { name: 'heart', fill: ESvgTypes.None },
      title: 'Private & judgement free',
      subtitle: 'Consent, comfort, and pleasure-first conversations every time.'
    },
    {
      svg: { name: 'gift', fill: ESvgTypes.None },
      title: 'Instant image & voice creation',
      subtitle: 'Generate custom images and voice notes. Made fast and on demand. Just ask. '
    },
    {
      svg: { name: 'crown', fill: ESvgTypes.None },
      title: 'Be truly VIP',
      subtitle: 'Get exclusive content available only through AI. Always be priority number 1.'
    }
  ];

  constructor(
    public profileService: ProfileService,
    private readonly profileStateService: ProfileStateService
  ) {}

  ngOnInit(): void {
    this.getTransactions();
  }

  getTransactions(): void {
    this.profileService
      .getUserTransactionsByCreatorId(this.selectedSubscription.creator_id)
      .pipe(first())
      .subscribe(res => {
        this.transactionsHistory = res;
        this.transactionsLoaded = true;
      });
  }

  onStateChange(state: boolean): void {
    const renewState = state ? 'on' : 'off';
    const payload = {
      auto_renew: renewState,
      subscription_id: this.selectedSubscription.id
    };
    this.selectedSubscription.state = state
      ? ESubscriptionState.Active
      : ESubscriptionState.Expiring;
    this.selectedSubscription.auto_renew = state;
    this.profileService
      .updateSubscription(payload)
      .pipe(first())
      .subscribe({
        next: () => {
          // Refresh user subscriptions in the prefetch service
          this.profileStateService.refreshUserSubscriptions();
          this.autoRenewChange.emit();
        },
        error: () => {
          this.selectedSubscription.auto_renew = !state;
          this.selectedSubscription.state = !state
            ? ESubscriptionState.Active
            : ESubscriptionState.Expiring;
        }
      });
  }

  openBillingHistory(): void {
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_Billing_History);
  }
}
